import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchDatosPlaneamientoCiclicos, createCiclico } from '../../../services/ciclicoService';

/**
 * Rango lunes → domingo (semana ISO 8601) para un año y n.º de semana.
 * El 4 de enero siempre pertenece a la semana 1 del año ISO indicado.
 */
function rangoSemanaISO(anioStr, semanaStr) {
  const anio = parseInt(anioStr, 10);
  const semana = parseInt(semanaStr, 10);
  if (Number.isNaN(anio) || Number.isNaN(semana) || semana < 1 || semana > 53) return null;

  const jan4 = new Date(anio, 0, 4);
  const day = jan4.getDay() || 7;
  const mondayW1 = new Date(jan4);
  mondayW1.setDate(jan4.getDate() - day + 1);
  const monday = new Date(mondayW1);
  monday.setDate(mondayW1.getDate() + (semana - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const toYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  return { inicio: toYmd(monday), fin: toYmd(sunday) };
}

function fechasDefaultSemana1() {
  const y = String(new Date().getFullYear());
  const r = rangoSemanaISO(y, '1');
  const iso = new Date().toISOString().slice(0, 10);
  return { inicio: r?.inicio ?? iso, fin: r?.fin ?? iso };
}

const CiclicoNuevo = () => {
  const navigate = useNavigate();
  const [plantillasAll, setPlantillasAll] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosLoading, setProyectosLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const hoy = new Date();

  const [nombre, setNombre] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [plantillaId, setPlantillaId] = useState('');
  const [anio, setAnio] = useState(String(hoy.getFullYear()));
  const [numeroSemana, setNumeroSemana] = useState('1');
  const [fechaInicio, setFechaInicio] = useState(() => fechasDefaultSemana1().inicio);
  const [fechaFin, setFechaFin] = useState(() => fechasDefaultSemana1().fin);

  useEffect(() => {
    const r = rangoSemanaISO(anio, numeroSemana);
    if (r) {
      setFechaInicio(r.inicio);
      setFechaFin(r.fin);
    }
  }, [anio, numeroSemana]);

  useEffect(() => {
    let ok = true;
    (async () => {
      setProyectosLoading(true);
      try {
        const bundle = await fetchDatosPlaneamientoCiclicos();
        if (ok) {
          setProyectos(bundle.proyectos);
          setPlantillasAll(bundle.plantillas);
        }
      } catch {
        if (ok) {
          setProyectos([]);
          setPlantillasAll([]);
        }
      } finally {
        if (ok) setProyectosLoading(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  const plantillas = useMemo(
    () =>
      plantillasAll.filter((pl) => {
        if (!proyectoId) return true;
        const pp = String(pl.proyectoId || '');
        /* Plantilla sin proyecto: disponible para cualquier cíclico con proyecto */
        if (!pp) return true;
        return pp === String(proyectoId);
      }),
    [plantillasAll, proyectoId],
  );

  useEffect(() => {
    if (!plantillaId) return;
    if (!plantillas.some((pl) => String(pl.id) === String(plantillaId))) {
      setPlantillaId('');
    }
  }, [plantillas, plantillaId]);

  const enviar = async (e) => {
    e.preventDefault();
    const n = nombre.trim();
    if (!n) {
      window.alert('Indique un nombre para el cíclico.');
      return;
    }
    if (!proyectoId) {
      window.alert('Seleccione un proyecto.');
      return;
    }
    if (!plantillaId) {
      window.alert('Seleccione una plantilla cíclica.');
      return;
    }
    if (!fechaInicio || !fechaFin) {
      window.alert('Indique fecha de inicio y fin del periodo.');
      return;
    }
    if (fechaFin < fechaInicio) {
      window.alert('La fecha fin debe ser posterior o igual a la fecha de inicio.');
      return;
    }

    setGuardando(true);
    try {
      const created = await createCiclico({
        nombre: n,
        proyectoId,
        plantillaId,
        anio: parseInt(anio, 10) || hoy.getFullYear(),
        numeroSemana: parseInt(numeroSemana, 10) || 1,
        fechaInicio,
        fechaFin,
      });
      navigate(`/produccion-concesionaria/planeamiento/ciclicos/${created.id}`, { replace: true });
    } catch (err) {
      window.alert(err.message || 'No se pudo crear el cíclico.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria/planeamiento/ciclicos" className="hover:text-teal-600">
          Cíclicos
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Nuevo</span>
      </nav>

      <h1 className="text-2xl font-bold text-gray-800 mb-2">Nuevo cíclico</h1>
      <p className="text-sm text-gray-600 mb-6">
        Elija <strong>año</strong> y <strong>n.º de semana</strong> (ISO): las fechas de inicio y fin se calculan solas
        (lunes a domingo de esa semana). El menú podrá completarse después en el detalle.
      </p>

      <form onSubmit={enviar} className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 space-y-4">
        <div>
          <label htmlFor="ciclo-nombre" className="block text-sm font-medium text-gray-700 mb-1">
            Nombre del cíclico
          </label>
          <input
            id="ciclo-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Ej. Ciclo abril — Cocina principal"
            required
          />
        </div>

        <div>
          <label htmlFor="ciclo-proyecto" className="block text-sm font-medium text-gray-700 mb-1">
            Proyecto
          </label>
          <select
            id="ciclo-proyecto"
            value={proyectoId}
            onChange={(e) => {
              setProyectoId(e.target.value);
              setPlantillaId('');
            }}
            disabled={proyectosLoading || proyectos.length === 0}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
            required
          >
            <option value="">
              {proyectosLoading
                ? 'Cargando…'
                : proyectos.length === 0
                  ? '— Sin proyectos —'
                  : '— Seleccione —'}
            </option>
            {proyectos.map((p) => {
              const base = p.code ? `${p.code} — ${p.name}` : p.name;
              const label = p.type === 'operacion' ? `${base} (operación)` : base;
              return (
                <option key={p.id} value={p.id}>
                  {label}
                </option>
              );
            })}
          </select>
          {!proyectosLoading && proyectos.length === 0 && (
            <p className="mt-1 text-xs text-amber-700">
              Cree proyectos u operaciones en{' '}
              <Link to="/configuration/projects" className="font-medium underline">
                Configuración → Proyectos
              </Link>
              .
            </p>
          )}
        </div>

        <div>
          <label htmlFor="ciclo-plantilla" className="block text-sm font-medium text-gray-700 mb-1">
            Plantilla cíclica
          </label>
          <select
            id="ciclo-plantilla"
            value={plantillaId}
            onChange={(e) => setPlantillaId(e.target.value)}
            disabled={!proyectoId}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
            required
          >
            <option value="">— Seleccione —</option>
            {plantillas.map((pl) => (
              <option key={pl.id} value={pl.id}>
                {pl.nombre}
              </option>
            ))}
          </select>
          {proyectoId && plantillas.length === 0 && (
            <p className="mt-1 text-xs text-amber-700">
              No hay plantillas para este proyecto. Cree una en{' '}
              <Link to="/produccion-concesionaria/planeamiento/plantillas-ciclicos" className="underline font-medium">
                Plantillas cíclicos
              </Link>
              .
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="ciclo-anio" className="block text-sm font-medium text-gray-700 mb-1">
              Año
            </label>
            <input
              id="ciclo-anio"
              type="number"
              min={2020}
              max={2100}
              value={anio}
              onChange={(e) => setAnio(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div>
            <label htmlFor="ciclo-semana" className="block text-sm font-medium text-gray-700 mb-1">
              N.º semana
            </label>
            <input
              id="ciclo-semana"
              type="number"
              min={1}
              max={53}
              value={numeroSemana}
              onChange={(e) => setNumeroSemana(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="ciclo-inicio" className="block text-sm font-medium text-gray-700 mb-1">
              Fecha inicio (lunes de la semana)
            </label>
            <input
              id="ciclo-inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              required
              title="Se actualiza al cambiar año y semana; puede ajustarla a mano si lo necesita"
            />
          </div>
          <div>
            <label htmlFor="ciclo-fin" className="block text-sm font-medium text-gray-700 mb-1">
              Fecha fin (domingo de la semana)
            </label>
            <input
              id="ciclo-fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              required
              title="Se actualiza al cambiar año y semana; puede ajustarla a mano si lo necesita"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 -mt-2">
          Semana ISO 8601: del lunes al domingo. Si cambia el año o el n.º de semana, las fechas se recalculan.
        </p>

        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={
              guardando || !proyectoId || plantillas.length === 0 || proyectos.length === 0 || proyectosLoading
            }
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {guardando ? 'Guardando…' : 'Crear cíclico'}
          </button>
          <Link
            to="/produccion-concesionaria/planeamiento/ciclicos"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
};

export default CiclicoNuevo;
